import type { Team } from '@rhythmic/db/types'

import { useRhythmicLogger } from '#imports'

import { getTeamCollection } from '../../getCollections'

import { assertWorkspace, normaliseString } from './useTeamContext.teamMutations.shared'

const logger = useRhythmicLogger('useTeamContext:updateTeam')

export interface UpdateTeamPayload {
  teamId: string
  name?: string
  slug?: string
  context?: string | null
  avatar_url?: string | null
}

export interface UpdateTeamContext {
  workspaceId: string
  workspaceSlug: string
  teams?: Team[]
}

function buildUpdateBody(payload: UpdateTeamPayload) {
  const body: Record<string, unknown> = {}

  const name = normaliseString(payload.name)
  const slug = normaliseString(payload.slug)

  if (name !== undefined) body.name = name
  if (slug !== undefined) body.slug = slug
  if (payload.context !== undefined) body.context = payload.context
  if (payload.avatar_url !== undefined) body.avatar_url = payload.avatar_url

  return body
}

function validateParams(payload: UpdateTeamPayload, context: UpdateTeamContext, updateBody: Record<string, unknown>) {
  const { teamId } = payload
  const { workspaceId, workspaceSlug, teams } = context

  assertWorkspace(workspaceId)
  assertWorkspace(workspaceSlug)

  if (!teamId) {
    const error = new Error('Team identifier is required')
    logger.error('Unable to update team: missing identifier', { workspaceId })
    throw error
  }

  if (Object.keys(updateBody).length === 0) {
    const error = new Error('At least one field must be provided to update the team')
    logger.warn('Update rejected: no changes supplied', { teamId })
    throw error
  }

  const trimmedSlug = typeof updateBody.slug === 'string' ? updateBody.slug : undefined
  if (trimmedSlug && Array.isArray(teams)) {
    const duplicate = teams.some((existing) => existing.slug === trimmedSlug && existing.id !== teamId)
    if (duplicate) {
      const error = new Error('A team with this slug already exists in the workspace')
      logger.warn('Unable to update team: duplicate slug detected', {
        teamId,
        slug: trimmedSlug,
      })
      throw error
    }
  }
  return { teamId, workspaceId, workspaceSlug, updateBody }
}

export default async function handleUpdateTeam(payload: UpdateTeamPayload, context: UpdateTeamContext) {
  try {
    const updateBody = buildUpdateBody(payload)
    const { teamId, workspaceId, workspaceSlug } = validateParams(payload, context, updateBody)
    const collection = getTeamCollection(workspaceSlug)
    const result = await collection.update(teamId, (draft: Team) => {
      Object.assign(draft as Record<string, unknown>, updateBody)
    })

    const updatedTeam = Array.isArray(result) ? result[0] : result

    logger.info('Team updated via collection', { teamId, workspaceId })
    return updatedTeam ?? null
  } catch (error) {
    logger.debug('updateTeam failed', { payload, context, error })
    logger.error('updateTeam failed', { error })
    if (error instanceof Error) throw error
    throw new Error('Failed to update team')
  }
}
